import type { FieldDefinition, Id } from "@estelle/domain";
import { useQuery } from "@tanstack/react-query";
import { ImageOff } from "lucide-react";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import {
  buildProductRow,
  projectProduct,
  type ProductRow,
} from "@/modules/catalog/projections";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { renderCell } from "@/components/table/cells";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { SelectionBar } from "@/components/table/SelectionBar";
import { selectionFacts, useSelection } from "@/components/table/selection";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Badge } from "@/components/ui/badge";
import {
  CatalogBulkActions,
  type BulkEditTarget,
} from "@/modules/catalog/screens/BulkActionBar";
import {
  ProductGrid,
  ProductTiles,
} from "@/modules/catalog/screens/ProductCards";
import { ProductDetail } from "@/modules/catalog/screens/ProductDetail";
import type { SectionScreenProps } from "@/modules/types";
import { useOpenRecord } from "@/routes/useOpenRecord";

/** Reused to format each end of a price range through the shared renderer. */
const PRICE_FIELD: FieldDefinition = {
  name: "price",
  label: "Price",
  type: "money",
};

function Thumbnail({ product }: { product: ProductRow }) {
  if (!product.featuredImageUrl) {
    return (
      <div
        className="bg-muted text-muted-foreground flex size-9 items-center justify-center rounded"
        aria-hidden
      >
        <ImageOff className="size-4" />
      </div>
    );
  }

  // Straight from Shopify's CDN and never re-hosted (FR-CAT-13) — this is
  // the one place in the app pointing an `<img>` at infrastructure we do
  // not own, which is the point: mirroring gigabytes of product photography
  // to serve it worse than Shopify already does buys nothing.
  return (
    <img
      src={product.featuredImageUrl}
      alt={product.title}
      loading="lazy"
      className="size-9 rounded object-cover"
    />
  );
}

function PriceRange({ product }: { product: ProductRow }) {
  if (product.priceMin === null || product.priceMax === null) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (product.priceMin === product.priceMax) {
    return <>{renderCell(PRICE_FIELD, product.priceMin)}</>;
  }
  return (
    <span className="whitespace-nowrap">
      {renderCell(PRICE_FIELD, product.priceMin)}
      {" – "}
      {renderCell(PRICE_FIELD, product.priceMax)}
    </span>
  );
}

function HsnCoverage({ product }: { product: ProductRow }) {
  if (product.variantCount === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (product.hsnMissingCount === 0) {
    return (
      <Badge variant="secondary" className="font-normal">
        All set
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="font-normal">
      {product.hsnMissingCount} missing
    </Badge>
  );
}

/**
 * The Products list (FR-CAT-1), the product detail it opens into (M6-2,
 * M6-3) and the bulk-edit selection over its rows (M6-5).
 *
 * `listProducts` returns the product row alone — variant count, price range
 * and HSN coverage are all derived from its variants, a separate resource.
 * Rather than teach the projection to reach for them (the one thing
 * `projections.ts` exists to prevent), this screen resolves each row's
 * variants once the page arrives and merges the result before anything is
 * projected or rendered. That costs one `getProduct` call per row on top of
 * the list call; against a fixture that settles in milliseconds this is
 * free, and a real API would more plausibly return these figures on the
 * list response directly, at which point the second query disappears
 * rather than needing to move anywhere.
 *
 * The same per-row `getProduct` fetch is also exactly what bulk-editing HSN
 * needs: every selected product's variants, with the `channelUpdatedAt`
 * each one's `setVariantHsn` write has to be guarded against.
 */
export function ProductList({
  fields,
  filter,
  search,
  sort,
  layout,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();

  const [limit, setLimit] = useState(25);
  /**
   * Paging stays local rather than joining filter, columns and sort in the
   * URL. A shared link should reproduce what someone was looking at, and
   * page four of a list that has since gained rows is not that.
   */
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];
  /**
   * Selection is local too, and per page rather than per filtered result
   * set — there is no `/catalog/:id` route to hold either (routes come from
   * `config/modules.ts`, which this module cannot edit), so which product is
   * open and which rows are checked both live here and both reset,
   * deliberately, on a page refresh.
   */
  const { recordId, open } = useOpenRecord();
  /**
   * The ticked products (#634), kept across pages and filter changes, each
   * with the variants its HSN edit reaches, captured when it was ticked so an
   * action can run on a product no longer on screen.
   */
  const selection = useSelection<Picked>((picked) => picked.row.id);
  const canBulkEdit = can(usePermissions(), "catalog.product.bulkEdit");

  const list = useQuery({
    queryKey: [
      "catalog",
      "products",
      "list",
      filter,
      search,
      sort,
      limit,
      cursor,
    ],
    queryFn: () =>
      data.catalog.listProducts({ filter, search, sort, limit, cursor }),
  });

  const ids = list.data?.items.map((product) => product.id) ?? [];

  const details = useQuery({
    queryKey: ["catalog", "products", "details", ids],
    queryFn: () => Promise.all(ids.map((id) => data.catalog.getProduct(id))),
    enabled: list.isSuccess,
  });

  if (list.isPending) return <TableSkeleton />;

  if (list.isError) {
    return (
      <LoadFailed
        message={list.error.message}
        onRetry={() => {
          void list.refetch();
        }}
      />
    );
  }

  if (details.isPending) return <TableSkeleton />;

  if (details.isError) {
    return (
      <LoadFailed
        message={details.error.message}
        onRetry={() => {
          void details.refetch();
        }}
      />
    );
  }

  const rows = list.data.items.map((product, index) =>
    buildProductRow(product, details.data[index] ?? null),
  );

  // Bulk HSN edit needs each selected product's variants (with their own
  // `channelUpdatedAt` to guard the write), which the row itself does not
  // carry — `ProductRow` is the display shape, `details.data` is where the
  // full `ProductWithVariants` this screen already fetched actually lives.
  const detailById = new Map(
    list.data.items.map(
      (product, index) => [product.id, details.data[index] ?? null] as const,
    ),
  );
  const Cards = layout === "grid" ? ProductGrid : ProductTiles;
  const pick = (row: ProductRow): Picked => {
    const detail = detailById.get(row.id) ?? null;
    return {
      row,
      target: detail ? { product: detail, variants: detail.variants } : null,
    };
  };
  const toggleSelected = (id: Id, checked: boolean) => {
    const existing = selection.rows.get(id);
    if (!checked && existing) {
      selection.set(existing, false);
      return;
    }
    const row = rows.find((candidate) => candidate.id === id);
    if (row) selection.set(pick(row), checked);
  };
  const selectedIds = new Set(selection.rows.keys());

  const targets: BulkEditTarget[] = [...selection.rows.values()].flatMap(
    (picked) => (picked.target ? [picked.target] : []),
  );
  const shownRows = selection.viewing
    ? [...selection.rows.values()].map((picked) => picked.row)
    : rows;
  const facts = selectionFacts({
    selection,
    pageRows: rows.map(pick),
    idOf: (picked) => picked.row.id,
  });

  // One element, handed to whichever layout is on screen (#382) — table and
  // cards each place it against their own scroll container, but there is
  // only ever one of them rendered at a time.
  const pager = (
    <Pagination
      page={list.data}
      limit={limit}
      onLimitChange={(next) => {
        setLimit(next);
        setHistory([undefined]);
      }}
      history={history}
      onNavigate={setHistory}
    />
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {recordId !== null ? (
        <RecordDrawer
          title={
            list.data?.items.find((product) => product.id === recordId)
              ?.title ?? "Product"
          }
        >
          <ProductDetail productId={recordId} />
        </RecordDrawer>
      ) : null}
      {layout !== "table" ? (
        <Cards
          rows={shownRows}
          detailById={detailById}
          selected={selectedIds}
          onSelectedChange={toggleSelected}
          onOpen={open}
          emptyState={<NoMatches onClear={onClearFilter} />}
          footer={selection.viewing ? undefined : pager}
        />
      ) : (
        <DataTable
          fields={fields}
          rows={shownRows}
          getRowId={(row) => row.id}
          project={projectProduct}
          sort={sort}
          onToggleSort={onToggleSort}
          onRowClick={(row) => {
            open(row.id);
          }}
          renderOverride={(field, row) => {
            switch (field.name) {
              case "thumbnail":
                return <Thumbnail product={row} />;
              case "priceRange":
                return <PriceRange product={row} />;
              case "hasHsn":
                return <HsnCoverage product={row} />;
              default:
                return undefined;
            }
          }}
          selection={
            canBulkEdit
              ? {
                  isSelected: (row) => selection.has(row.id),
                  onToggle: (row, on) => {
                    toggleSelected(row.id, on);
                  },
                  onTogglePage: (pageRows, on) => {
                    selection.setMany(pageRows.map(pick), on);
                  },
                  labelOf: (row) => `Select ${row.title}`,
                  active: selection.size > 0,
                }
              : undefined
          }
          emptyState={<NoMatches onClear={onClearFilter} />}
          footer={selection.viewing ? undefined : pager}
        />
      )}

      {canBulkEdit ? (
        <SelectionBar
          selection={selection}
          onPage={facts.onPage}
          notInView={facts.notInView}
          pageFullySelected={facts.pageFullySelected}
          matching={list.data.total}
          noun="products"
          actions={<CatalogBulkActions targets={targets} />}
        />
      ) : null}
    </div>
  );
}

/** A selected product, with the variants its HSN edit reaches (#634). */
interface Picked {
  row: ProductRow;
  target: BulkEditTarget | null;
}
